import { arcRows, sameNumber } from '../../../utils/arcRows';
// The base no-unused-vars rule does not see type-only uses.
// eslint-disable-next-line no-unused-vars
import { ArcEditRow, ArcRow, LanguagePair } from '../../../utils/arcRows';
import { describeError as describeApiError } from '../../../utils/serverErrors';
// eslint-disable-next-line no-unused-vars
import { ApiError } from '../../../utils/serverErrors';
// eslint-disable-next-line no-unused-vars
import { LatestChangeSet } from '../ChangeSets/changeSets';
import { CURRENCIES } from '../Pricing/constants';

export const BASE_RATES_ROUTE = '/settings/baseRate';

export interface BasePrice {
  languagePair: LanguagePair;
  price: {
    amount: number;
    currency: string;
  };
}

export interface BaseRates extends LatestChangeSet {
  organizationId: number;
  version: number;
  author: string;
  // ISO-8601 UTC timestamp of when this version was created.
  createdAt?: string;
  basePrices: BasePrice[];
}

type ValueField = 'amount' | 'currency';

export type RateRow = ArcRow<ValueField>;

export type EditRow = ArcEditRow<BasePrice, ValueField>;

// Pricing accepts only these on base-rate writes.
const BASE_RATE_CURRENCIES = [...CURRENCIES].sort();

const CURRENCY_REQUIRED = `Choose ${BASE_RATE_CURRENCIES.join(' or ')}.`;

const isPositiveNumber = (value: string): boolean => {
  const trimmed = value.trim();
  const amount = Number(trimmed);
  return trimmed !== '' && Number.isFinite(amount) && amount > 0;
};

const ACCESS_DENIED_MESSAGE = 'You do not have permission to manage base rates.';

export const rateRows = arcRows<BasePrice, ValueField>({
  columns: [
    {
      key: 'amount',
      header: 'Amount',
      numeric: true,
      same: sameNumber,
      validate: (value): string | undefined =>
        isPositiveNumber(value) ? undefined : 'Enter a positive number.',
    },
    {
      key: 'currency',
      header: 'Currency',
      options: BASE_RATE_CURRENCIES,
      placeholder: 'Choose a currency',
      // Uploads may write a currency in any case.
      normalize: (value): string => value.toUpperCase(),
      validate: (value): string | undefined =>
        BASE_RATE_CURRENCIES.includes(value) ? undefined : CURRENCY_REQUIRED,
    },
  ],
  valuesOf: ({ price }) => ({ amount: String(price.amount), currency: price.currency }),
  itemOf: (languagePair, { amount, currency }) => ({
    languagePair,
    price: { amount: Number(amount), currency },
  }),
  collection: 'basePrices',
  rejectedMessage: 'Pricing rejected the base rates.',
  accessDeniedMessage: ACCESS_DENIED_MESSAGE,
});

export const CONFLICT_HEADER = 'Rates changed since you loaded them; reload';

export const describeError = (error: ApiError): string[] =>
  describeApiError(error, ACCESS_DENIED_MESSAGE);
